import React, { useEffect } from 'react';

import styled from 'styled-components';

import { MenuItemCustomization } from 'components/menu-order-wizard/menu-item-customization';
import SelectionContainer from 'components/selection-container';
import { useFormatCalories } from 'hooks/use-format-calories';
import { Upsell } from 'pages/cart/upsell';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import {
  formattedCalorieMapForPickerAspectCalorieMap,
  isCombo,
  pickerAspectOptHasResolution,
  reducePickerCalorieMapToRanges,
} from 'utils/menu';

import useModalCta from './hooks/use-modal-cta';
import PickerDetail from './picker-detail';
import { useWizard } from './wizard-provider';
import { getComboSlotId, getIsComboInSeries, getShouldShowModifiers } from './wizard-ui-helpers';

const StyledUpsell = styled.div`
  margin-bottom: 2rem;
`;

function ParallelUI({
  addToOrder,
  addToOrderText,
  calorieMap = {},
  comboSlotSelections,
  currentPrice,
  excludesForPickerAspects = [],
  handleComboSelection,
  handleComboSlotSelectionQuantityUpdate,
  handleModifierQuantityUpdate,
  handleModifierSelection,
  handlePickerSelection,
  modifierSelections,
  pickerAspects,
  pickerOptions,
  pickerSelection,
  aspectPromotionMap,
  pricingFunction,
  quickConfigs = [],
  selectedPickerIdentifiers,
  setModalCta,
}) {
  const useEnableMenuUpsellItems = useFlag(LaunchDarklyFlag.ENABLE_MENU_UPSELL_ITEMS);
  const { showStaticMenu } = useMenuContext();
  const formatCalories = useFormatCalories();
  const {
    actions: { incrementStep },
    state: { currentStep, isFinalStep },
  } = useWizard();

  useModalCta({
    addToOrder,
    addToOrderText,
    currentPrice,
    incrementStep,
    isFinalStep,
    setModalCta,
  });

  useEffect(
    () => {
      tagAspectOptionsForAvailabilty();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPickerIdentifiers]
  );

  let calculatedComboSlots = [];
  const comboSlots = isCombo(pickerSelection)
    ? pickerSelection.options.filter(
        slot =>
          slot.uiPattern !== 'hidden' &&
          slot.options.some(opt => pricingFunction(opt.option) !== undefined)
      )
    : [];
  const isComboInSeries = getIsComboInSeries(pickerSelection);
  let showModifiers = true;
  // if the pickerSelection is a combo in series only show one comboSlot at a time when we need to.
  if (comboSlots.length && isComboInSeries) {
    const hasMainItemAndMods = !!pickerSelection.mainItem?.options.length;
    // subtract the one step for pickerAspects
    const currentStepAfterPickerAspects = currentStep - 1;
    const comboSlotId = getComboSlotId(
      comboSlots,
      currentStepAfterPickerAspects,
      hasMainItemAndMods
    );
    if (comboSlotId) {
      calculatedComboSlots = [comboSlotId];
    }
    showModifiers = getShouldShowModifiers(currentStepAfterPickerAspects);
  } else {
    calculatedComboSlots = comboSlots.map(({ _id }) => _id);
  }

  // Only show the item customization on the first step if our selection is not a combo in series
  const showSelectionCustomization = currentStep === 0 ? !isComboInSeries : true;

  const tagAspectOptionsForAvailabilty = () => {
    pickerAspects.map(pickerAspect => {
      return pickerAspect.pickerAspectOptions.map(option => {
        const otherSelections = Object.values({
          ...selectedPickerIdentifiers,
          [pickerAspect._id]: undefined,
        }).filter(filteredOption => filteredOption);
        option.disabled = !pickerAspectOptHasResolution(
          option.identifier,
          otherSelections,
          pickerOptions
        );
        return option;
      });
    });
  };

  // Only show the pickerAspects on the first step for parallel.
  return (
    <div>
      {currentStep === 0 &&
        pickerAspects.map((aspect, index) => {
          const selectedPickerAspectOption = (aspect.pickerAspectOptions || []).find(
            option => option.identifier === selectedPickerIdentifiers[aspect._id]
          );

          const calorieMapForPickerAspect = Object.values(selectedPickerIdentifiers)
            .slice(0, index)
            .reduce((map, identifier) => (map ? map[identifier] : null), calorieMap);

          const calorieRangesForCurrentPickerAspect =
            reducePickerCalorieMapToRanges(calorieMapForPickerAspect);

          return (
            <SelectionContainer
              focusOnMount={index === 0}
              key={aspect._id}
              required={!showStaticMenu}
              title={aspect.name?.locale}
              uiPattern={aspect.uiPattern}
              selection={selectedPickerAspectOption?.name?.locale ?? ''}
            >
              <PickerDetail
                aspect={aspect}
                aspectPromotionMap={aspectPromotionMap}
                calorieMap={
                  formattedCalorieMapForPickerAspectCalorieMap(
                    calorieRangesForCurrentPickerAspect,
                    formatCalories
                  ) || {}
                }
                handleSelection={handlePickerSelection}
                selectedPickerIdentifiers={selectedPickerIdentifiers}
                isInStaticMenu={showStaticMenu}
              />
            </SelectionContainer>
          );
        })}
      {showSelectionCustomization && pickerSelection && (
        <>
          <MenuItemCustomization
            comboSlotSelections={comboSlotSelections}
            excludesForPickerAspects={excludesForPickerAspects}
            handleComboSelection={handleComboSelection}
            handleComboSlotSelectionQuantityUpdate={handleComboSlotSelectionQuantityUpdate}
            handleModifierQuantityUpdate={handleModifierQuantityUpdate}
            handleModifierSelection={handleModifierSelection}
            modifierSelections={modifierSelections}
            menuItem={pickerSelection}
            pricingFunction={pricingFunction}
            quickConfigs={quickConfigs}
            comboSlotsToShow={calculatedComboSlots}
            showModifiers={showModifiers}
          />
          {useEnableMenuUpsellItems && (
            <StyledUpsell>
              <Upsell />
            </StyledUpsell>
          )}
        </>
      )}
    </div>
  );
}

export default ParallelUI;
